import React, { createContext, useContext } from 'react'
import { supabase } from '../supabase'
import { etichettaVerso } from './ordinePartita'

// ── SOCIETA CONTEXT ────────────────────────────────────────
// Il filtro per società è la NORMA (dal 10/10/2026): `createScopedDb` aggiunge da sé la
// condizione `societa_id` a select/update/delete e la inserisce in insert/upsert per OGNI
// tabella, senza elenchi da tenere aggiornati. Qui sotto ci sono SOLO le eccezioni, ognuna
// con il motivo scritto.
//
// Prima era il contrario (esisteva `TENANT_TABLES`, un elenco di tabelle DA filtrare):
// una tabella nuova restava scoperta senza che nessuno se ne accorgesse — è successo tre
// volte, l'ultima con `documenti` (408 certificati di tutti i club leggibili da un
// amministratore, 10/10/2026).
//
// REGOLA: si AGGIUNGE sempre, non si toglie mai. Nessuna tabella che era filtrata prima del
// 10/10/2026 smette di esserlo: c'è un test in shared.test.js che lo verifica una per una.
// Se una tabella è nuova e non compare qui, viene filtrata da sola: è il comportamento voluto.
const NON_FILTRATE = new Set([
  // ── a) relazioni che NON hanno la colonna `societa_id` ──────────────────────────────
  // Filtrarle sarebbe un errore: il database risponderebbe "column societa_id does not exist".
  'societa',                     // è la tabella delle società: il suo id È la società
  'sito_societa',                // vista senza societa_id (la usa il sito pubblico)
  'piani',                       // listino dei piani: vale per tutti i club
  'permessi_ruolo',              // permessi per ruolo: vale per tutti i club
  'ruoli_giocatori',             // elenco dei ruoli: vale per tutti i club
  'categorie_partite',           // categorie delle partite: valgono per tutti i club
  'impostazioni_globali',        // impostazioni generali, non di un club
  'private_config',              // configurazione di servizio (la tocca solo la chiave di servizio)
  'richieste_pubbliche',         // conteggio anti-abuso dei form pubblici (solo dal server)
  'comunicazioni_lette',         // stato di lettura per utente
  'formazioni',                  // formazioni delle partite
  'rate',                        // rate
  'sessioni_utente',             // stato delle sessioni
  'user_layouts',                // disposizione delle schermate per utente

  // ── b) tabelle DI SOCIETÀ che NON devono passare dal filtro automatico ──────────────
  // Ognuna è una decisione scritta, non una dimenticanza. Si rivedono, non si aggiungono
  // senza motivo (il controllo automatico si accorge se una di queste è nuova).
  'avversari',                   // anagrafica CONDIVISA della federazione (decisione 19/09):
                                 //   l'associazione avversario↔società passa da tornei_squadre/partite
  'profili',                     // la ricerca del profilo al login è per id, prima di conoscere la società
  'wallet',                      // il superamministratore deve leggere TUTTE le società (abbonamenti)
  'wallet_movimenti',            // idem
  'wallet_addebiti_mensili',     // idem (e non è usata nel frontend)
])

function createScopedDb(societaId) {
  if (!societaId) return supabase
  return new Proxy(supabase, {
    get(target, prop) {
      if (prop !== 'from') return typeof target[prop] === 'function' ? target[prop].bind(target) : target[prop]
      return (table) => {
        const builder = target.from(table)
        // Filtro per difetto: si salta SOLO quello che è nell'elenco delle eccezioni.
        if (NON_FILTRATE.has(table)) return builder
        return new Proxy(builder, {
          get(bt, bp) {
            if (bp === 'insert') {
              return (data) => {
                const rows = Array.isArray(data) ? data : [data]
                return bt.insert(rows.map(r => ({ ...r, societa_id: societaId })))
              }
            }
            if (bp === 'upsert') {
              return (data, opts) => {
                const rows = Array.isArray(data) ? data : [data]
                return bt.upsert(rows.map(r => ({ ...r, societa_id: societaId })), opts)
              }
            }
            if (bp === 'select' || bp === 'update' || bp === 'delete') {
              return (...args) => bt[bp](...args).eq('societa_id', societaId)
            }
            return typeof bt[bp] === 'function' ? bt[bp].bind(bt) : bt[bp]
          }
        })
      }
    }
  })
}

const SocietaContext = createContext({ societaId: null, societa: null, db: supabase })
// eslint-disable-next-line no-unused-vars
const useSocieta = () => useContext(SocietaContext)

const RED = "#D0021B"
const GOLD = "#9B7D2E"
const LIGHT = "#fdf8f8"

const CATEGORIE_GRUPPI = [
  { gruppo: 'Prima Squadra',    items: ['Prima Squadra'] },
  { gruppo: 'Juniores',         items: ['Juniores'] },
  { gruppo: 'Settore Giovanile',items: ['Under 18','Under 17','Under 16','Under 15','Under 14'] },
  { gruppo: 'Scuola Calcio',    items: ['Under 13','Under 12','Under 11','Under 10','Under 9','Pulcini','Piccoli Amici','Scuola Calcio'] },
]
const CATEGORIE_FLAT = CATEGORIE_GRUPPI.flatMap(g => g.items)

// Anni di nascita per categoria, RIFERITI alla stagione 2025/2026 (anno di inizio
// 2025). Sono numerici così da slittare automaticamente: ad ogni nuova stagione
// (confine 1° luglio) le annate scorrono di un anno, uguale per TUTTE le società.
// `modo`: 'precedenti' → "AAAA e precedenti"; 'plus' → "AAAA+".
const CATEGORIE_ANNI_REF = 2025
const CATEGORIE_ANNI = {
  'Prima Squadra':        {},
  'Juniores':             { anno1: 2006, modo: 'precedenti' },
  'Under 18':             { anno1: 2008 },
  'Under 17':             { anno1: 2009 },
  'Under 16':             { anno1: 2010 },
  'Under 15':             { anno1: 2011 },
  'Under 14':             { anno1: 2012 },
  'Under 13':             { anno1: 2013 },
  'Under 12':             { anno1: 2014 },
  'Under 11':             { anno1: 2015 },
  'Under 10':             { anno1: 2016 },
  'Under 9':              { anno1: 2017 },
  'Pulcini':              { anno1: 2018 },
  'Piccoli Amici':        { anno1: 2019, anno2: 2020 },
  'Scuola Calcio':        { anno1: 2021, modo: 'plus' },
}

// Anno di inizio della stagione sportiva corrente (confine 1° luglio).
const annoInizioStagione = (oggi = new Date()) => {
  const y = oggi.getFullYear()
  return oggi.getMonth() >= 6 ? y : y - 1
}
// Anni di nascita (già slittati alla stagione corrente) per una categoria standard.
const anniCategoria = (cat, oggi = new Date()) => {
  const def = CATEGORIE_ANNI[cat]
  if (!def || def.anno1 == null) return null
  const shift = annoInizioStagione(oggi) - CATEGORIE_ANNI_REF
  return { anno1: def.anno1 + shift, anno2: def.anno2 != null ? def.anno2 + shift : null, modo: def.modo || null }
}
// Categoria standard attesa per un anno di nascita nella stagione corrente
// (slitta automaticamente ogni stagione). Anni più vecchi della fascia Under 18
// → Juniores (la scelta Juniores/Prima Squadra resta poi manuale); anni nella
// fascia più giovane → Scuola Calcio. Ritorna null se non determinabile.
function categoriaPerAnnoNascita(annoNascita, oggi = new Date()) {
  const anno = Number(annoNascita)
  if (!anno) return null
  const shift = annoInizioStagione(oggi) - CATEGORIE_ANNI_REF
  for (const [cat, def] of Object.entries(CATEGORIE_ANNI)) {
    if (def.anno1 == null || def.modo) continue
    if (anno === def.anno1 + shift || (def.anno2 != null && anno === def.anno2 + shift)) return cat
  }
  const sc = CATEGORIE_ANNI['Scuola Calcio']
  if (sc?.anno1 != null && anno >= sc.anno1 + shift) return 'Scuola Calcio'
  const u18 = CATEGORIE_ANNI['Under 18']
  if (u18?.anno1 != null && anno < u18.anno1 + shift) return 'Juniores'
  return null
}

// Stringa annata per una categoria standard: "2009", "2006 e precedenti", "2021+", "2019/2020".
const annoNascitaLabel = (cat, oggi = new Date()) => {
  const an = anniCategoria(cat, oggi)
  if (!an) return null
  if (an.modo === 'precedenti') return `${an.anno1} e precedenti`
  if (an.modo === 'plus') return `${an.anno1}+`
  if (an.anno2) return `${an.anno1}/${an.anno2}`
  return String(an.anno1)
}
const catLabel = (cat, row) => {
  if (!cat) return '—'
  // Categorie standard: annata calcolata dalla stagione corrente (slitta ogni anno,
  // uguale per tutte le società) — ha priorità su eventuali valori salvati vecchi.
  const lab = annoNascitaLabel(cat)
  if (lab) return `${cat} (${lab})`
  // Categorie personalizzate (non standard): usa l'anno salvato se presente.
  const a1 = row?.anno_nascita_1
  const a2 = row?.anno_nascita_2
  if (a1 && a2) return `${cat} (${a1}/${a2})`
  if (a1) return `${cat} (${a1})`
  return cat
}

// Ordina un array di squadre dalla categoria più alta (Prima Squadra) alla più bassa
// (Scuola Calcio / Miste), usando l'ordine di CATEGORIE_FLAT. Le squadre con categoria
// sconosciuta o nulla vanno in fondo. A parità di categoria ordina per nome_personalizzato.
const ordinaSquadre = (arr) => {
  if (!Array.isArray(arr)) return arr
  const idx = (s) => {
    const i = CATEGORIE_FLAT.indexOf(s?.categoria || s?.nome || '')
    return i === -1 ? 9999 : i
  }
  return [...arr].sort((a, b) => {
    const d = idx(a) - idx(b)
    if (d !== 0) return d
    return (a?.nome_personalizzato || '').localeCompare(b?.nome_personalizzato || '')
  })
}

const sqLabel = (s) => {
  if (!s) return '—'
  const base = s.categoria || s.nome || '—'
  return s.nome_personalizzato ? `${base} — ${s.nome_personalizzato}` : base
}

/**
 * Le categorie inferiori da cui un allenatore può "promuovere" atleti fuori età.
 *
 * ATTENZIONE ALLE CHIAVI: sono CATEGORIE, non nomi di squadra.
 *
 * Fino al 07/10/2026 la ricerca si faceva sul NOME della squadra: funzionava solo per le squadre
 * chiamate esattamente "Under 14", "Juniores", "Prima Squadra"… e NON per tutte le altre — che sono
 * la maggioranza: "Under 14 GIOVANISSIMI B", "ALLIEVI B", "TERZA CATEGORIA", "Juniores Elite",
 * "Esordienti I° Anno". Effetto segnalato da Stefano il 07/10/2026: nelle convocazioni il riquadro
 * dei fuori età ("la squadra della convocazione e quella dell'anno precedente") non compariva più.
 */
const CATEGORIE_INFERIORI = {
  'Prima Squadra': ['Juniores', 'Under 18'],
  'Juniores': ['Under 18'],
  'Under 18': ['Under 17'],
  'Under 17': ['Under 16'],
  'Under 16': ['Under 15'],
  'Under 15': ['Under 14'],
  'Under 14': ['Under 13'],
  'Under 13': ['Under 12'],
  'Under 12': ['Under 11'],
  'Under 11': ['Under 10'],
  'Under 10': ['Under 9'],
  'Under 9': ['Pulcini'],
  'Pulcini': ['Piccoli Amici'],
  'Piccoli Amici': [],
}

/**
 * Le categorie inferiori di una squadra. Si guarda la CATEGORIA (il dato affidabile) e, se manca,
 * si ripiega sul nome (comportamento di prima, per non perdere nessun caso).
 */
const categorieInferioriDi = (squadra) => {
  const s = squadra || {}
  return CATEGORIE_INFERIORI[s.categoria] || CATEGORIE_INFERIORI[s.nome] || []
}

/** Una squadra femminile non pesca atleti dalle squadre maschili (e viceversa). */
const squadraFemminile = (s) =>
  /femminil/i.test(`${s?.nome || ''} ${s?.nome_personalizzato || ''}`)

/**
 * Fra le squadre della società, quali sono quelle della categoria inferiore?
 *
 * Regole, in ordine:
 *  1. stesso "genere" della squadra della convocazione (femminile con femminile, maschile con
 *     maschile): senza questo, l'Under 15 femminile proporrebbe i maschi dell'Under 14;
 *  2. si preferisce la squadra che si chiama esattamente come la categoria richiesta (era il
 *     comportamento di prima, dove funzionava);
 *  3. poi quella della stessa stagione e infine una qualsiasi della categoria giusta.
 */
const scegliSquadreInferiori = (candidati, categorie, femminile, stagione = null) => {
  const stesse = (candidati || []).filter((s) => squadraFemminile(s) === !!femminile)
  return (categorie || [])
    .map((cat) =>
      stesse.find((s) => s.nome === cat) ||
      (stagione ? stesse.find((s) => s.categoria === cat && s.stagione === stagione) : null) ||
      stesse.find((s) => s.categoria === cat))
    .filter(Boolean)
}

const statoColor = (s) => {
  if (["Attivo","Pagato","Valido","Firmato"].includes(s)) return { bg: "#e6f4ea", color: "#1a7a3a" }
  if (["In attesa","In scadenza","Rateizzato"].includes(s)) return { bg: "#fff8e1", color: "#b36a00" }
  if (["Scaduto","Squalificato","Mancante","Inattivo"].includes(s)) return { bg: "#fdecea", color: "#c0392b" }
  if (s === "Infortunato") return { bg: "#fff3e0", color: "#e67e22" }
  if (s === "Alta") return { bg: "#fdecea", color: "#c0392b" }
  if (s === "Bassa") return { bg: "#e6f4ea", color: "#1a7a3a" }
  return { bg: "#f0f0f0", color: "#555" }
}
const Badge = ({ label }) => {
  const { bg, color } = statoColor(label)
  return <span style={{ background: bg, color, borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 500 }}>{label}</span>
}
const STATI_TESSERAMENTO = ['Non Tesserato', 'Pre-tesserato', 'Tesserato', 'In Prova']
const coloreTesseramento = (st) => (
  st === 'Tesserato' ? { bg: '#e6f4ea', color: '#1a7a3a' }
  : st === 'Pre-tesserato' ? { bg: '#e8f0fe', color: '#1565c0' }
  : st === 'In Prova' ? { bg: '#fff3e0', color: '#e67e22' }
  : st === 'Scaduto' ? { bg: '#fff8e1', color: '#b36a00' }
  : { bg: '#fdecea', color: '#c0392b' }
)
const Card = ({ children, style, onClick, ...rest }) => (
  <div onClick={onClick} style={{ background: "white", borderRadius: 12, border: "0.5px solid #e0e0e0", padding: "1rem 1.25rem", ...style }} {...rest}>{children}</div>
)
const MetricCard = ({ label, value, color, onClick, active }) => (
  <div onClick={onClick} style={{ background: active ? '#fff5f5' : '#f9f6f6', borderRadius: 8, padding: '0.9rem 1rem', flex: 1, minWidth: 100, cursor: onClick ? 'pointer' : 'default', border: active ? `1.5px solid ${color || RED}` : '1.5px solid transparent', transition: 'all 0.15s', userSelect: 'none' }}>
    <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{label} {onClick ? (active ? '▲' : '▼') : ''}</div>
    <div style={{ fontSize: 24, fontWeight: 500, color: color || RED }}>{value}</div>
  </div>
)
const SectionHeader = ({ title, btnLabel, onBtn }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
    <h2 style={{ fontSize: 20, fontWeight: 500, color: "#222", margin: 0 }}>{title}</h2>
    {btnLabel && <button onClick={onBtn} style={{ background: RED, color: "white", border: "none", borderRadius: 8, padding: "7px 18px", cursor: "pointer", fontSize: 13 }}>{btnLabel}</button>}
  </div>
)
const FilterBar = ({ options, value, onChange }) => (
  <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
    {options.map(o => (
      <button key={o} onClick={() => onChange(o)} style={{ border: `1px solid ${value === o ? RED : "#ddd"}`, background: value === o ? RED : "white", color: value === o ? "white" : "#555", borderRadius: 20, padding: "4px 14px", fontSize: 12, cursor: "pointer" }}>{o}</button>
    ))}
  </div>
)
const Spinner = () => (
  <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}>
    <div style={{ fontSize: 28, marginBottom: 8 }}>⚽</div>
    <div style={{ fontSize: 13 }}>Caricamento...</div>
  </div>
)

const normalizzaTel = (tel) => {
  if (!tel) return ''
  let t = String(tel).replace(/\s+/g, '').replace(/[().\-]/g, '') // eslint-disable-line no-useless-escape
  if (t.startsWith('+')) t = t.slice(1)
  else if (t.startsWith('0039')) t = t.slice(2)
  else if (t.startsWith('39') && t.length >= 11) {} // già prefissato
  else if (t.startsWith('3') && t.length >= 9) t = '39' + t
  return t
}
const formatDate = (d) => {
  if (!d) return '—'
  const s = String(d).split('T')[0]
  const parts = s.split('-')
  if (parts.length !== 3) return d
  return `${parts[2]}/${parts[1]}/${parts[0]}`
}

const apriWA = (tel, msg = '') => {
  const num = normalizzaTel(tel)
  if (!num) return
  window.open(`https://wa.me/${num}${msg ? `?text=${encodeURIComponent(msg)}` : ''}`, '_blank')
}
const BtnWA = ({ tel, msg = '', style = {} }) => {
  const num = normalizzaTel(tel)
  if (!num) return null
  return (
    <button onClick={(e) => { e.stopPropagation(); apriWA(tel, msg) }}
      title={`WhatsApp: ${tel}`}
      style={{ background: '#25D366', color: 'white', border: 'none', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 500, ...style }}>
      WhatsApp
    </button>
  )
}

const validaCF = cf => !cf || /^[A-Z0-9]{16}$/i.test(cf)

// ── Utility per inviti utenti (usate da GestioneUtenti e dalla scheda Genitori in Giocatori) ──
const DEFAULT_APP_URL = 'https://sportly.vercel.app/'

// Carica URL app + nome società + password temporanea di default dalla tabella "impostazioni".
// societaId viene dal profilo utente loggato — serve per l'anteprima WhatsApp (logo del club).
const caricaParametriInvito = async (supabase) => {
  const [{ data: urlRow }, { data: pwRow }, userRes] = await Promise.all([
    supabase.from('impostazioni').select('valore').eq('chiave', 'app_url').maybeSingle(),
    supabase.from('impostazioni').select('valore').eq('chiave', 'password_temporanea').maybeSingle(),
    supabase.auth.getUser(),
  ])
  const userId = userRes?.data?.user?.id
  let societaId = null
  if (userId) {
    const { data: prof } = await supabase.from('profili').select('societa_id').eq('id', userId).maybeSingle()
    societaId = prof?.societa_id || null
  }
  // Dati della società per la firma in fondo alle email di invito: nome, indirizzo, CAP,
  // città, telefono, email, sito.
  // ⚠️ NON stanno nella tabella `societa`: il club li scrive dalla pagina "Società" e vivono
  // in `impostazioni` con le chiavi `societa_*`, come per tutte le stampe. Prima si leggeva
  // `societa` chiedendo due colonne che non esistono (`cap`, `sito`): la richiesta falliva
  // con un 400 e la firma dell'email restava senza indirizzo (10/10/2026).
  let societa = null
  if (societaId) {
    const { data: righe } = await supabase
      .from('impostazioni')
      .select('chiave,valore')
      .eq('societa_id', societaId)
      .in('chiave', SOCIETA_FIELDS.map(([k]) => `societa_${k}`))
    societa = Object.fromEntries((righe || []).map((r) => [String(r.chiave).replace(/^societa_/, ''), r.valore || '']))
  }
  return {
    appUrl: urlRow?.valore || DEFAULT_APP_URL,
    nomeSocieta: societa?.nome || '',
    passwordTemporanea: pwRow?.valore || 'sportly2026',
    societaId,
    societa,
  }
}

// Messaggio per richiesta visita medica sportiva (certificato in scadenza o scaduto).
// Usato dai bottoni "Richiedi visita medica" nelle schede giocatore/allenatore e dai
// pannelli scadenze nella Dashboard.
const messaggioRichiestaVisita = ({ nome, cognome, scadenza, nomeSocieta, scaduto }) => {
  const intestazione = scaduto
    ? `⚠️ IMPORTANTE: Certificato medico scaduto`
    : `⚠️ Certificato medico in scadenza`
  const motivo = scaduto
    ? `il certificato medico sportivo di ${cognome || ''} ${nome || ''} è SCADUTO il ${formatDate(scadenza)}.

Fino a quando non consegnerai il nuovo certificato, non sarà possibile convocare l'atleta alle partite.`
    : `il certificato medico sportivo di ${cognome || ''} ${nome || ''} scadrà il ${formatDate(scadenza)}.

Ti chiediamo di prenotare al più presto la visita medica sportiva per rinnovarlo.`
  return `${intestazione}

Ciao,
${motivo}

📅 Cosa fare:
1) Prenota una visita medica sportiva presso un centro autorizzato
2) Ritira il nuovo certificato
3) Consegna o invia una foto del certificato alla società

Grazie per la collaborazione.
${nomeSocieta || 'La società'}`
}

// Messaggio di invito per WhatsApp / Email — pensato per utenti non tecnici.
// Struttura: saluto → link → credenziali in evidenza → 3 passi numerati.
// La password va mostrata in chiaro e isolata su una riga, con l'avviso sulla
// maiuscola automatica del telefono (causa n.1 di login falliti).
// Etichette leggibili dei ruoli, usate nel messaggio di invito per dire al
// destinatario che tipo di accesso gli è stato attivato.
const RUOLI_LABEL = {
  admin: 'Amministratore',
  allenatore: 'Allenatore',
  dirigente: 'Dirigente',
  segreteria: 'Segreteria',
  ospite: 'Ospite (accesso di prova)',
  genitore: 'Genitore',
  atleta: 'Atleta',
  marketing: 'Marketing',
  superadmin: 'Superadmin',
}

const messaggioInvito = ({ nome, email, password, appUrl, nomeSocieta, societaId, societa = null, ruolo = null, linkCorto = false }) => {
  // WhatsApp: link /invito/<id> (lungo) → serve per l'anteprima con logo del club.
  // Email: link corto (home) → più pulito da leggere, tanto in mail l'anteprima non serve.
  const base = (appUrl || '').replace(/\/+$/, '')
  const linkApp = linkCorto ? (base + '/') : (societaId ? `${base}/invito/${societaId}` : (appUrl || base + '/'))
  const saluto = `Ciao ${nome || ''}! 👋
${nomeSocieta || 'La società'} ti ha attivato l'accesso al gestionale online.${ruolo ? `
👤 Tipo di accesso: ${RUOLI_LABEL[ruolo] || ruolo}` : ''}`

  const credenziali = password
    ? `

🔑 I TUOI DATI PER ENTRARE

Email:
${email}

Password:
${password}

⚠️ Scrivi la password ESATTAMENTE com'è, rispettando maiuscole e minuscole. Attenzione: il telefono spesso mette la prima lettera maiuscola da solo — controllala prima di premere "Accedi".`
    : `

🔑 I TUOI DATI PER ENTRARE

Email:
${email}

Password:
quella che hai scelto al primo accesso

Non la ricordi? Rispondi a questo messaggio e te la reimpostiamo in un minuto.`

  const istruzioni = `

👉 COME ENTRARE (3 passi)

1) Tocca questo link:
${linkApp}

2) Scrivi la tua email e la password qui sopra.

3) Tocca "Accedi". Al primo ingresso ti verrà chiesto di scegliere una password personale: da quel momento userai quella.

📱 Vuoi l'icona sul telefono come le altre app?
Nella pagina di accesso tocca il pulsante "Come installare l'app sul telefono" e segui la guida: 2 minuti.`

  // Firma della società: solo nelle email (linkCorto), con i dati di contatto del club.
  // Rende la mail riconoscibile e riduce il rischio che finisca nello spam.
  let firma = ''
  if (linkCorto) {
    const righe = []
    const nomeClub = societa?.nome || nomeSocieta
    if (nomeClub) righe.push(nomeClub)
    const indirizzo = [societa?.indirizzo, [societa?.cap, societa?.citta].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    if (indirizzo) righe.push(indirizzo)
    const contatti = [
      societa?.telefono ? `Tel: ${societa.telefono}` : null,
      societa?.email ? `Email: ${societa.email}` : null,
      societa?.sito || null,
    ].filter(Boolean).join(' · ')
    if (contatti) righe.push(contatti)
    if (righe.length) firma = `

—
${righe.join('\n')}`
  }

  return saluto + credenziali + istruzioni + firma
}

const SOCIETA_FIELDS = [
  ['nome',        'Nome società'],
  ['indirizzo',   'Indirizzo'],
  ['cap',         'CAP'],
  ['citta',       'Città'],
  ['telefono',    'Telefono'],
  ['email',       'Email'],
  ['sito',        'Sito internet'],
  ['piva',        'Partita IVA'],
  ['cf',          'Codice fiscale'],
  ['figc',        'N° iscrizione federale FIGC'],
  ['coni',        'N° affiliazione CONI'],
  ['campo_casa',  'Campo casa (preimpostato)'],
  ['presidente',  'Nome Presidente'],
]

// Matrice permessi per ruolo:
//  - admin       → tutto
//  - allenatore  → Presenze, Calendario, Convocazioni, Comunicazioni, Giocatori (sua squadra)
//  - atleta      → Profilo (proprio), Calendario, Convocazioni (ricevute)
//  - genitore    → Profilo (figlio), Pagamenti, Documenti, Comunicazioni
//  - segreteria  → come dirigente ma con accesso pieno alla gestione amministrativa
//  - dirigente/superadmin mantenuti per retro-compatibilità
// NB: questi valori sono i DEFAULT. I permessi effettivi vengono poi caricati dalla
// tabella "permessi_ruolo" nel database, modificabile dalla pagina "Permessi" (admin).
// Le voci sono raggruppate per "sezione" — usata per mostrare titoletti nel menù laterale.
// L'ordine delle voci è anche quello visualizzato (da cima a fondo).
const NAV_ITEMS = [
  // ── Panoramica ──
  { id: "dashboard", label: "Dashboard", icon: "⊞", section: "Panoramica", ruoli: ["admin","allenatore","dirigente","segreteria","ospite","superadmin","marketing"] },
  // "Cose da fare": quello che è in sospeso adesso, calcolato dai dati (migrazione 20261004).
  // Sta subito dopo la Dashboard perché è la prima cosa da guardare quando si apre il gestionale.
  { id: "cosedafare", label: "Cose da fare", icon: "🔔", section: "Panoramica", ruoli: ["admin","dirigente","segreteria","superadmin"] },
  { id: "superadmin", label: "Società & Tenant", icon: "🏟️", section: "Panoramica", ruoli: ["superadmin"] },
  { id: "abbonamenti", label: "Gestione abbonamenti", icon: "💳", section: "Panoramica", ruoli: ["superadmin"] },
  { id: "piani", label: "Listino piani", icon: "💼", section: "Panoramica", ruoli: ["superadmin"] },
  { id: "profilo", label: "Profilo", icon: "🙋", section: "Panoramica", ruoli: ["atleta","genitore"] },
  { id: "accademia", label: "Accademy", icon: "🎓", section: "Panoramica", ruoli: ["admin","allenatore","atleta","dirigente","genitore","segreteria","ospite","superadmin","marketing"] },
  // ── Rosa ──
  { id: "squadre", label: "Squadre", icon: "⚽", section: "Rosa", ruoli: ["admin","allenatore","dirigente","segreteria","ospite","superadmin"] },
  { id: "giocatori", label: "Atleti", icon: "👤", section: "Rosa", ruoli: ["admin","allenatore","dirigente","segreteria","ospite","superadmin"] },
  { id: "allenatori", label: "Allenatori", icon: "📋", section: "Rosa", ruoli: ["admin","dirigente","segreteria","ospite","superadmin"] },
  { id: "dirigenti", label: "Dirigenti e Staff", icon: "🏅", section: "Rosa", ruoli: ["admin","segreteria","ospite","superadmin"] },
  // ── Sportiva ──
  { id: "calendario", label: "Calendario", icon: "🗓️", section: "Sportiva", ruoli: ["admin","allenatore","atleta","dirigente","segreteria","ospite","superadmin"] },
  { id: "presenze", label: "Allenamenti", icon: "✅", section: "Sportiva", ruoli: ["admin","allenatore","superadmin"] },
  { id: "convocazioni", label: "Convocazioni", icon: "📣", section: "Sportiva", ruoli: ["admin","allenatore","atleta","superadmin"] },
  { id: "tornei", label: "Campionati e Tornei", icon: "🏆", section: "Sportiva", ruoli: ["admin","allenatore","dirigente","segreteria","ospite","superadmin"] },
  { id: "statistiche", label: "Statistiche atleti", icon: "📊", section: "Sportiva", ruoli: ["admin","allenatore","dirigente","segreteria","ospite","superadmin"] },
  { id: "disciplina", label: "Disciplina", icon: "⚠️", section: "Sportiva", ruoli: ["admin","allenatore","dirigente","segreteria","ospite","superadmin"], featFlag: "feat_disciplina" },
  { id: "infortuni", label: "Infortuni", icon: "🩹", section: "Sportiva", ruoli: ["admin","allenatore","dirigente","segreteria","ospite","superadmin"], featFlag: "feat_infortuni" },
  // ── Amministrazione ──
  { id: "wallet", label: "Wallet & Abbonamento", icon: "👛", section: "Amministrazione", ruoli: ["admin","superadmin"] },
  { id: "pagamenti", label: "Pagamenti", icon: "💶", section: "Amministrazione", ruoli: ["admin","dirigente","genitore","segreteria","ospite","superadmin"] },
  { id: "documenti", label: "Documenti", icon: "📄", section: "Amministrazione", ruoli: ["admin","dirigente","genitore","segreteria","ospite","superadmin"] },
  { id: "sponsor", label: "Sponsor", icon: "🤝", section: "Amministrazione", ruoli: ["admin","segreteria","ospite","superadmin","marketing"] },
  { id: "abbigliamento", label: "Abbigliamento", icon: "👕", section: "Amministrazione", ruoli: ["admin","dirigente","segreteria","ospite","superadmin"], featFlag: "feat_abbigliamento" },
  { id: "compensi", label: "Compensi", icon: "💰", section: "Amministrazione", ruoli: ["admin","segreteria","ospite","superadmin"], featFlag: "feat_compensi" },
  { id: "centriestivi", label: "Centri Estivi", icon: "☀️", section: "Amministrazione", ruoli: ["admin","dirigente","segreteria","ospite","superadmin"], featFlag: "feat_centri_estivi" },
  // ── Comunicazione ──
  { id: "comunicazioni", label: "Comunicazioni", icon: "✉️", section: "Comunicazione", ruoli: ["admin","allenatore","dirigente","genitore","segreteria","ospite","superadmin","marketing"] },
  { id: "email", label: "Email", icon: "📧", section: "Comunicazione", ruoli: ["admin","segreteria","ospite","superadmin"], societaFlag: 'modulo_email' },
  { id: "stampe", label: "Stampe & PDF", icon: "🖨️", section: "Comunicazione", ruoli: ["admin","dirigente","segreteria","ospite","superadmin"] },
  { id: "news", label: "News (sito pubblico)", icon: "📰", section: "Comunicazione", ruoli: ["admin","segreteria","superadmin","marketing"], featFlag: "feat_sito" },
  // ── Sistema ──
  { id: "utenti", label: "Gestione utenti", icon: "🔐", section: "Sistema", ruoli: ["admin","superadmin"] },
  { id: "permessi", label: "Permessi", icon: "🛡️", section: "Sistema", ruoli: ["admin","superadmin"] },
  { id: "configurazione", label: "Configurazione", icon: "🔧", section: "Sistema", ruoli: ["admin","superadmin"] },
  { id: "impostazioni", label: "Società", icon: "🏠", section: "Sistema", ruoli: ["admin","allenatore","atleta","dirigente","genitore","segreteria","ospite","superadmin","marketing"] },
]

// Default page per ruolo (primo modulo visibile dopo il login)
const DEFAULT_PAGE_BY_RUOLO = {
  admin: "cosedafare",
  superadmin: "dashboard",
  dirigente: "cosedafare",
  segreteria: "cosedafare",
  ospite: "dashboard",
  allenatore: "presenze",
  atleta: "profilo",
  genitore: "profilo",
  marketing: "news",
}

// Tutti i ruoli gestibili (usati nei menu a tendina)
const RUOLI_DISPONIBILI = ["admin","allenatore","dirigente","segreteria","ospite","genitore","atleta","marketing"]

// ── Comuni italiani: dataset locale (7894 comuni con CAP, da gardainformatica.it) ──
// Caricato via dynamic import → chunk separato, on-demand
let _comuniCache = null
let _comuniPromise = null
const loadComuni = async () => {
  if (_comuniCache) return _comuniCache
  if (_comuniPromise) return _comuniPromise
  _comuniPromise = (async () => {
    try {
      const mod = await import('../data/comuni.json')
      _comuniCache = mod.default || mod
      return _comuniCache
    } catch (e) {
      console.error('[comuni] import failed:', e)
      _comuniCache = []
      return []
    } finally {
      _comuniPromise = null
    }
  })()
  return _comuniPromise
}
// Pulisco eventuale cache vecchia di v1 (fetch online)
try { localStorage.removeItem('sportly_comuni_v1') } catch {}
// Estrae sigle uniche ordinate
const sigleProvincia = (comuni) => [...new Set((comuni || []).map(c => c.sigla).filter(Boolean))].sort()
// Filtra comuni per sigla provincia
const comuniByProvincia = (comuni, sigla) => (comuni || []).filter(c => c.sigla === sigla).sort((a, b) => a.nome.localeCompare(b.nome))
// Trova un comune per nome (case-insensitive)
const trovaComune = (comuni, nome) => (comuni || []).find(c => c.nome?.toLowerCase() === (nome || '').toLowerCase())

// Calcola il codice fiscale italiano a partire dai dati anagrafici.
// Richiede: nome, cognome, data_nascita (YYYY-MM-DD), sesso (M|F), comune_nascita, provincia_nascita (sigla).
// Ritorna { cf } oppure { errore }.
const calcolaCodiceFiscale = async ({ nome, cognome, data_nascita, sesso, comune_nascita, provincia_nascita }) => {
  if (!nome || !cognome) return { errore: 'Nome e cognome mancanti' }
  if (!data_nascita) return { errore: 'Data di nascita mancante' }
  if (!sesso || !['M', 'F'].includes(sesso)) return { errore: 'Sesso mancante (M o F)' }
  if (!comune_nascita) return { errore: 'Comune di nascita mancante' }
  const d = new Date(data_nascita)
  if (isNaN(d.getTime())) return { errore: 'Data di nascita non valida' }
  try {
    const mod = await import('codice-fiscale-js')
    const CF = mod.default || mod
    const cfObj = new CF({
      name: nome,
      surname: cognome,
      gender: sesso,
      day: d.getDate(),
      month: d.getMonth() + 1,
      year: d.getFullYear(),
      birthplace: comune_nascita,
      birthplaceProvincia: provincia_nascita || '',
    })
    return { cf: cfObj.cf }
  } catch (e) {
    return { errore: 'Comune non riconosciuto o dati incompleti: ' + (e.message || e) }
  }
}

// Livelli di permesso per modulo. Ordine crescente di privilegio.
// - nessuno  : il modulo non è visibile nel menu
// - lettura  : l'utente vede la pagina ma non può modificare/creare/eliminare
// - aggiunta : l'utente vede e può creare nuovi record, ma non può modificare/eliminare quelli esistenti
// - completo : l'utente può creare, modificare, eliminare
const LIVELLI_PERMESSO = ['nessuno', 'lettura', 'aggiunta', 'completo']
const LIVELLO_LABEL = { nessuno: 'Nessuno', lettura: 'Lettura', aggiunta: 'Aggiunta', completo: 'Completo' }

// Contesto per esporre i permessi effettivi dell'utente loggato alle singole pagine.
// Valore atteso: { livelloPerModulo: { giocatori: 'completo', pagamenti: 'lettura', ... }, isAdmin: bool }
const PermessiContext = React.createContext({ livelloPerModulo: {}, isAdmin: true })

// Contesto per avviare una guida passo-passo da QUALSIASI pagina.
// La funzione arriva da App.js, che sa cambiare pagina: serve ai passi che devono
// portare l'utente su un'altra schermata (es. dalla Accademy a Pagamenti).
// Valore atteso: { apriGuida: (passi) => void }
const GuidaContext = createContext({ apriGuida: () => {} })

// DOVE SI GIOCA — la riga che va nella distinta di gara (richiesta di Stefano, 04/10/2026:
// "nella distinta della partita ci deve essere la dicitura del campo dove si gioca, che sia in casa
// che sia fuori casa").
//
// I dati vengono dal calendario federale (bollettino), che indica impianto, indirizzo e comune di
// ogni partita: in casa è il nostro campo, in trasferta è quello dell'avversario.
// Se il campo non si sa ancora lo dice ("campo da confermare") invece di stampare il NOSTRO campo:
// era l'errore di prima, perché `partite.campo` conteneva il campo di casa anche in trasferta.
//
// Restituisce testo semplice: si usa sia a schermo sia nelle stampe.
const rigaCampo = (partita) => {
  const p = partita || {}
  const inCasa = String(p.luogo || '').toLowerCase().startsWith('casa')
  const nome = p.campo_nome || (inCasa ? p.campo : '') || ''
  const pezzi = [nome, p.campo_indirizzo, p.campo_citta].filter(Boolean)
  const verso = p.luogo ? etichettaVerso(inCasa) : ''
  if (!pezzi.length) return `${verso ? verso + ' — ' : ''}campo da confermare`
  return `${verso ? verso + ' — ' : ''}${pezzi.join(' · ')}`
}

// ── Squadre avversarie: anti-duplicati con confronto fuzzy ──
// Pulisce il nome: minuscolo, rimuove punteggiatura e prefissi/suffissi tipici di società sportive
// (FC, A.S.D., U.S., Football Club, Calcio, ecc.) per poter confrontare i nomi "puri".
const normalizzaNomeAvversario = (s) => {
  if (!s) return ''
  return String(s)
    .toLowerCase()
    // eslint-disable-next-line no-useless-escape
    .replace(/[\.\-'`]/g, '')
    .replace(/\b(fc|asd|ssd|us|ac|ss|sg|gs|pol|polisportiva|asdd|csi|associazione|sportiva|dilettantistica|dilett|football|club|calcio|usd|csd|gsd|upd|acd|ad|sc|scsd|ssdarl|ssdrl|asdc|sd)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

// Similarità "trigram" (0..1): set di triplette di caratteri / Jaccard.
// 1.0 = identici dopo normalizzazione, 0.6 ≈ simili al 60%.
const _trigrammi = (s) => {
  const out = new Set()
  if (!s) return out
  const padded = `  ${s} `
  for (let i = 0; i < padded.length - 2; i++) out.add(padded.slice(i, i + 3))
  return out
}
const similNomi = (a, b) => {
  const A = _trigrammi(normalizzaNomeAvversario(a))
  const B = _trigrammi(normalizzaNomeAvversario(b))
  if (A.size === 0 || B.size === 0) return 0
  let inter = 0
  for (const t of A) if (B.has(t)) inter++
  return inter / (A.size + B.size - inter)
}

// Cerca tra gli avversari già esistenti uno simile (≥ soglia, default 0.6) al nome cercato.
// Preferisce sempre quelli con matricola_figc. Esclude le squadre B (SQ.B, SQ.2 ecc).
const trovaAvversarioSimile = (nomeRaw, lista, soglia = 0.6) => {
  const nome = (nomeRaw || '').trim()
  if (!nome || !lista?.length) return null
  // Le "squadre B" non vanno fuse con la squadra principale: stessa società, ma squadra diversa.
  if (/\b(SQ\.?\s?B|SQ\.?\s?2|SQUADRA\s?B|SQUADRA\s?2)\b/i.test(nome)) return null
  const norm = normalizzaNomeAvversario(nome)
  if (!norm) return null
  let best = null
  let bestScore = 0
  for (const a of lista) {
    if (!a?.nome) continue
    if (/\b(SQ\.?\s?B|SQ\.?\s?2|SQUADRA\s?B|SQUADRA\s?2)\b/i.test(a.nome)) continue
    const score = similNomi(nome, a.nome)
    if (score < soglia) continue
    if (score > bestScore) { best = a; bestScore = score; continue }
    // A parità di punteggio, preferisci quello con matricola
    if (score === bestScore && a.matricola_figc && best && !best.matricola_figc) best = a
  }
  return best
}

// Intervallo di date [inizio, fine] (ISO YYYY-MM-DD) di una stagione sportiva
// con nome "YYYY/YYYY": va dal 1° luglio del primo anno al 30 giugno del secondo.
// Usato per filtrare presenze/allenamenti/partite della stagione (dopo il 30/6
// la nuova stagione riparte pulita, senza dati della precedente). Ritorna null
// se il nome non è nel formato atteso (in tal caso: nessun filtro stagione).
function stagioneRange(nome) {
  const m = String(nome || '').match(/^(\d{4})\/(\d{4})$/)
  if (!m) return null
  return { start: `${m[1]}-07-01`, end: `${m[2]}-06-30` }
}

// Scadenza tesseramento FIGC: 30 giugno di fine della stagione sportiva
// rispetto a una data di riferimento (default: oggi). Un tesseramento fatto da
// luglio in poi scade il 30/6 dell'anno successivo; da gennaio a giugno scade
// il 30/6 dello stesso anno (fine della stagione in corso). Ritorna 'YYYY-06-30'.
function scadenzaTesseramento(dataRif) {
  const d = dataRif ? new Date(dataRif) : new Date()
  if (isNaN(d.getTime())) return null
  const y = d.getFullYear()
  return `${d.getMonth() >= 6 ? y + 1 : y}-06-30`
}

// Crea (o riattiva, se già esistente) la stagione `nome` per la società corrente
// e la imposta come attiva, disattivando le altre. Idempotente: rispetta il
// vincolo unique (societa_id, nome) e non genera duplicati. `db` è lo scoped
// client del SocietaContext (inietta societa_id).
async function attivaStagione(db, nome) {
  const n = String(nome || '').trim()
  if (!n) return { data: null, error: { message: 'Nome stagione mancante' } }
  await db.from('stagioni').update({ attiva: false }).eq('attiva', true)
  const { data: esistente } = await db.from('stagioni').select('*').eq('nome', n).maybeSingle()
  if (esistente) {
    return db.from('stagioni').update({ attiva: true }).eq('id', esistente.id).select().single()
  }
  return db.from('stagioni').insert([{ nome: n, attiva: true }]).select().single()
}

// Apre un file dello storage Supabase generando un signed URL a breve scadenza
// (i bucket sono privati: niente URL pubblici permanenti). Accetta sia il path
// ("<societaId>/<id>.pdf") sia un URL pubblico storico salvato nei record vecchi,
// da cui estrae il path.
async function apriFileStorage(bucket, pathOrUrl) {
  if (!pathOrUrl) return
  const path = pathOrUrl.startsWith('http') ? pathOrUrl.split(`/${bucket}/`).pop() : pathOrUrl
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 120)
  if (error || !data?.signedUrl) { alert('Errore apertura file: ' + (error?.message || 'URL non disponibile')); return }
  window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
}

export {
  stagioneRange, scadenzaTesseramento, attivaStagione, apriFileStorage,
  NON_FILTRATE, createScopedDb, SocietaContext, useSocieta,
  PermessiContext, LIVELLI_PERMESSO, LIVELLO_LABEL,
  GuidaContext,
  RED, GOLD, LIGHT,
  CATEGORIE_GRUPPI, CATEGORIE_FLAT, anniCategoria, annoNascitaLabel, categoriaPerAnnoNascita, catLabel, sqLabel, ordinaSquadre,
  CATEGORIE_INFERIORI, categorieInferioriDi, scegliSquadreInferiori, squadraFemminile,
  Badge, STATI_TESSERAMENTO, coloreTesseramento, Card, MetricCard, SectionHeader, FilterBar, Spinner,
  normalizzaTel, formatDate, apriWA, BtnWA, rigaCampo,
  NAV_ITEMS, DEFAULT_PAGE_BY_RUOLO, RUOLI_DISPONIBILI,
  SOCIETA_FIELDS, validaCF,
  DEFAULT_APP_URL, caricaParametriInvito, messaggioInvito, messaggioRichiestaVisita,
  loadComuni, sigleProvincia, comuniByProvincia, trovaComune, calcolaCodiceFiscale,
  trovaAvversarioSimile,
}
